import { resolveLocalizedValue } from "@robe/core-utils";
import {
  loginRequest,
  logoutRequest,
  RequestContext,
  BaseApiRequest,
  coreFetch,
  BaseFetchOptions,
  LoginParams,
  getTopLevelCategories,
  GetTopLevelCategoriesOptions,
  invalidateCache,
  Generate2faQRCodeParams,
  generate2faQRCode,
} from "@robe/core-api";
import { ContentValue, Localized, TopLevelCategory } from "@robe/core-types";
import { User } from "@robe/core-types/user";
import { XcoreContent } from "./XcoreContent";
import { XcoreContentType } from "./XcoreContentType";
import { XcoreFiles } from "./XcoreFiles";
import { XcoreGroupsPermissions } from "./XcoreGroupsPermissions";
import { XcoreRouting } from "./XcoreRouting";
import { XcoreSubCategory } from "./XcoreSubCategory";
import { XcoreUser } from "./XcoreUser";
import { XcoreWeb } from "./XcoreWeb";
import { XcoreInquiry } from "./XcoreInquiry";

export * from "./XcoreContent";
export * from "./XcoreFiles";
export * from "./XcoreRouting";
export * from "./XcoreSubCategory";
export * from "./XcoreUser";
export * from "./XcoreInquiry";
export * from "./XcoreWeb";

export class XcoreCore implements RequestContext {
  private _ctx: RequestContext;

  public content: XcoreContent;
  public contentType: XcoreContentType;
  public subCategory: XcoreSubCategory;
  public routing: XcoreRouting;
  public files: XcoreFiles;
  public user: XcoreUser;
  public groupsPermissions: XcoreGroupsPermissions;
  public inquiries: XcoreInquiry;
  public web: XcoreWeb;

  public constructor(ctx: RequestContext) {
    this._ctx = ctx;

    this.content = new XcoreContent(this);
    this.contentType = new XcoreContentType(this);
    this.subCategory = new XcoreSubCategory(this);
    this.routing = new XcoreRouting(this);
    this.files = new XcoreFiles(this);
    this.user = new XcoreUser(this);
    this.groupsPermissions = new XcoreGroupsPermissions(this);
    this.inquiries = new XcoreInquiry(this);
    this.web = new XcoreWeb(this);
  }

  public getCtx() {
    return this._ctx;
  }

  public getSerializableCtx() {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { requestHandler: _, authToken: __, ...ctx } = this._ctx;
    return ctx;
  }

  public login(params: LoginParams): BaseApiRequest<User> {
    return loginRequest(this, params);
  }

  public logout(): BaseApiRequest<Response> {
    return logoutRequest(this);
  }

  public generate2faQRCode(params: Generate2faQRCodeParams): BaseApiRequest<Response> {
    return generate2faQRCode(this, params);
  }

  public listTopLevelCategories(options?: GetTopLevelCategoriesOptions): BaseApiRequest<TopLevelCategory[]> {
    return getTopLevelCategories(this, options);
  }

  // todo throw error here
  public fetch(req: string, options?: Partial<BaseFetchOptions>): BaseApiRequest<Response> {
    return coreFetch(req, { ...this._ctx, ...options });
  }

  public resolveLocalizedValue<T extends ContentValue>(localizedResource: Localized<T>) {
    return resolveLocalizedValue(this.currentLocale, this.locales)(localizedResource);
  }

  public invalidateCache(): BaseApiRequest<Response> {
    return invalidateCache(this);
  }

  /**
   * @returns Array consisting of current locale and fallback locale (if locale has one configured) for which content should be fetched.
   * This is not a part of xcore config json.
   */
  public get localeContent(): string[] {
    if (this.currentLocale in this.locales && this.locales[this.currentLocale]?.fallbackContentLocale) {
      return [this.currentLocale, this.locales[this.currentLocale].fallbackContentLocale as string];
    }

    return [this.currentLocale];
  }

  public get locales() {
    return this._ctx.locales;
  }
  public get currentLocaleConfig() {
    return this._ctx.locales[this.currentLocale];
  }
  public get currentLocale() {
    return this._ctx.currentLocale;
  }
  public get apiUrl() {
    return this._ctx.apiUrl;
  }
  public get mainLocaleDomain() {
    return this._ctx.locales[this.currentLocale]?.domains?.[0] ?? null;
  }

  /** HttpOnly cookie so it is accessible only in deployed app */
  public get authToken() {
    return this._ctx.authToken;
  }
  public get requestHandler() {
    return this._ctx.requestHandler;
  }
}
